import { useQuery, useMutation } from "convex/react";
import { PageHeader } from "../PageHeader";
import { PRRiskBriefCard, type PRRiskBrief } from "../shared/PRRiskBriefCard";
import { api } from "../../../lib/convexApi";

// Spec 5.1 PR 2 — PRRiskPage cuts from fetchPRRiskBriefs() to
// useQuery(api.risk.queries.recentBriefs). The Convex query returns
// {briefs: [{id, prUrl, score, summary, factors, createdAt}]}; we
// adapt that into the legacy PRRiskBrief shape for the existing
// card component. analyzePr → Spec 5 PR 10 webhook (out of scope
// for this PR — the table is fed by manual admin invocation +
// GitHub webhook integration that ships next).
//
// DEFERRED (C72-3, Spec 5.1 PR3+): the producer side of PR risk
// (POST /api/analyze) still writes to the legacy JsonStore.addPrScan
// rather than calling api.risk.actions.analyzePr. Until the producer
// migrates, environments that rely on the REST analyzer will see an
// empty Convex briefs list. Manual admin/webhook-driven analyzePr
// calls do land in Convex and are visible here today. Tracking
// follow-up: docs/superpowers/specs/2026-05-26-spec5-route-migration.md
// task list (PR3 GitHub webhook → analyzePr wiring).

interface ConvexBrief {
  id: string;
  prUrl: string;
  score: number;
  summary: string;
  factors: Array<{ id: string; reason: string; weight: number }>;
  createdAt: number;
  reviewed: boolean;
}

function parsePrUrl(url: string): { repo: string; prNumber: number; htmlUrl: string } {
  const match = url.match(/github\.com\/([^/]+\/[^/]+)\/pull\/(\d+)/i);
  if (match) {
    return { repo: match[1], prNumber: Number(match[2]), htmlUrl: url };
  }
  return { repo: url, prNumber: 0, htmlUrl: url };
}

function severityFromScore(score: number): PRRiskBrief["severity"] {
  if (score >= 70) return "high";
  if (score >= 40) return "medium";
  return "low";
}

function adapt(brief: ConvexBrief, idx: number): PRRiskBrief {
  const { repo, prNumber, htmlUrl } = parsePrUrl(brief.prUrl);
  return {
    id: brief.id || `pr-${idx}`,
    repo,
    prNumber,
    title: brief.summary,
    severity: severityFromScore(brief.score),
    rules: brief.factors.map((f) => ({ id: f.id, summary: f.reason })),
    recommended: [],
    htmlUrl,
    reviewed: brief.reviewed,
  };
}

export function PRRiskPage() {
  const data = useQuery(api.risk.queries.recentBriefs, { limit: 100 }) as
    | { briefs: ConvexBrief[] }
    | undefined;
  const markReviewed = useMutation(api.risk.mutations.markReviewed);

  if (!data) {
    return (
      <>
        <PageHeader title="PR risk briefs" subtitle="loading…" />
        <div className="sr-page-body">
          <div className="sr-empty">Loading PR risk briefs…</div>
        </div>
      </>
    );
  }

  const briefs = data.briefs.map(adapt);
  const highCount = briefs.filter((b) => b.severity === "high").length;

  function handleMarkReviewed(id: string) {
    // Fire-and-forget: a failure here just leaves the "Mark reviewed" button
    // visible (retriable, no data loss) — but the promise still needs a
    // catch, or a real failure (e.g. a brief deleted between page load and
    // click) becomes an unhandled promise rejection in the console.
    void markReviewed({ id, reviewed: true }).catch((err) => {
      console.error("Failed to mark PR risk brief reviewed:", err);
    });
  }

  return (
    <>
      <PageHeader
        title="PR risk briefs"
        subtitle={
          briefs.length > 0 ? `${highCount} high-risk · ${briefs.length} total` : "no PR data"
        }
      />
      <div className="sr-page-body">
        {briefs.length === 0 && (
          <div className="sr-pr-empty">
            <p>
              <strong>No PR data yet.</strong>
            </p>
            <p>
              PR risk needs a connected GitHub repository to receive
              <code> pull_request </code> webhooks.
            </p>
            <a className="sr-btn" href="/?settings=integrations">
              Connect GitHub in Admin → Integrations
            </a>
          </div>
        )}
        {briefs.map((b) => (
          <PRRiskBriefCard key={b.id} brief={b} onMarkReviewed={handleMarkReviewed} />
        ))}
      </div>
    </>
  );
}
